import { useQuery } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { OrderService } from '../services/order-service';
import httpClient from '../services/http-client';
import { OrderSummaryDto } from '../types';
import { useDivisionContext } from '../contexts/division-context';

// Create an order service instance using the default HTTP client
const orderService = new OrderService(httpClient);

/**
 * Hook to get orders requiring approval for the current user
 * Uses the selected division from DivisionContext (header selector)
 */
export function useUserApprovals() {
  const { isAuthenticated, tokenClaims } = useAuth();
  const { selectedDivisionId, hasMultipleDivisions } = useDivisionContext();
  
  // Extract all division IDs from token claims for reference
  const tokenDivisionIds = tokenClaims?.divisionId || tokenClaims?.division_id;
  const allDivisionIds = Array.isArray(tokenDivisionIds) ? tokenDivisionIds : (tokenDivisionIds ? [tokenDivisionIds] : []);
  
  // Debug logging
  console.log('useUserApprovals - Using selected division:', {
    selectedDivisionId,
    allDivisionIds,
    hasMultipleDivisions,
    isAuthenticated
  });
  
  const query = useQuery({
    queryKey: ['user-approvals', selectedDivisionId],
    queryFn: async (): Promise<OrderSummaryDto[]> => {
      if (!selectedDivisionId) {
        throw new Error('No division ID selected');
      }
      return orderService.getOrdersRequiringApproval(selectedDivisionId);
    },
    enabled: isAuthenticated && !!selectedDivisionId,
    staleTime: 15 * 1000, // 15 seconds for approval data
    retry: (failureCount, error) => {
      // Don't retry if there's no division ID
      if (error?.message?.includes('No division ID')) {
        return false;
      }
      return failureCount < 3;
    }
  });

  return {
    ...query,
    divisionId: selectedDivisionId,
    hasMultipleDivisions,
    allDivisionIds
  };
}

/**
 * Hook to get orders requiring approval for a specific division
 * (for users with multiple divisions)
 */
export function useUserApprovalsForDivision(divisionId: string) {
  const { isAuthenticated, tokenClaims } = useAuth();
  
  // Extract division IDs from token claims
  const tokenDivisionIds = tokenClaims?.divisionId || tokenClaims?.division_id;
  const allDivisionIds = Array.isArray(tokenDivisionIds) ? tokenDivisionIds : (tokenDivisionIds ? [tokenDivisionIds] : []);
  
  // Check if user has access to the requested division
  const hasAccessToDivision = allDivisionIds.includes(divisionId);
  
  return useQuery({
    queryKey: ['user-approvals', divisionId],
    queryFn: async (): Promise<OrderSummaryDto[]> => {
      if (!hasAccessToDivision) {
        throw new Error(`No access to division ${divisionId}`);
      }
      return orderService.getOrdersRequiringApproval(divisionId);
    },
    enabled: isAuthenticated && !!divisionId && hasAccessToDivision,
    staleTime: 15 * 1000, // 15 seconds for approval data
  });
}